import { bool, object, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { getExpansionDateKey, getDateKey } from "@repo/structure";
import { getWeekOfMonth } from "date-fns";
import { useEstimateEditorSettings, useEstimateStructure } from "@tools";
import { formatDay } from "@tools/EstimateEditor/utils/index";
import { TooltipIcon } from "@components";
import { capitalize } from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import classnames from "classnames";
import useStyle from "./TimelineExpandingComponent.style";

export const TimelineExpandingWeekHeader = observer(({
  language,
  month,
  week,
  expansions,
}) => {
  const structure = useEstimateStructure();
  const { blockExpansions, isPdfPrinterView } = useEstimateEditorSettings();
  const { t } = useTranslation();
  const classes = useStyle();
  const isExpanded = Boolean(
    expansions?.get(getExpansionDateKey(week.startDate, false)),
  );

  const toggleExpanded = () => {
    if (blockExpansions) return;
    structure.settings.setTimelineWeekExpanded(
      month.startDate,
      week.startDate,
      !isExpanded,
    );
  };

  return (
    <div className={classnames(
      classes.weekHeader,
      isExpanded && classes.activeWeek,
      isExpanded && "preset-active-2",
    )}>
      <div
        className={classnames(
          classes.label,
          !isExpanded && classes.collapsed,
        )}
      >
        {`${capitalize(t("time.week"))} ${getWeekOfMonth(week.startDate)}`}
        {!isPdfPrinterView && (
          <TooltipIcon
            icon={
              <ExpandMore
                className={classnames(
                  "expand transition -rotate-90",
                  isExpanded && "rotate-90",
                )}
              />
            }
            onClick={toggleExpanded}
            className={classnames(
              "noDrag",
              classes.expandButton,
              isExpanded ? classes.expandButtonExpanded : "weekHoverIcon",
            )}
            color="primary"
          >
            {t(
              `views.editor.timeline_table.${
                isExpanded ? "hide" : "show"
              }_day`,
            )}
          </TooltipIcon>
        )}
      </div>
      {isExpanded && (
        <div className="flex h-full">
          {week.days.map((day) => (
            <div className={classes.label} key={getDateKey(day)}>
              {formatDay(day, language)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

TimelineExpandingWeekHeader.propTypes = {
  month: object.isRequired,
  week: object.isRequired,
  expansions: object.isRequired,
  language: string,
};

TimelineExpandingWeekHeader.displayName = "TimelineExpandingWeekHeader";
